import { Component, Inject, Input } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { UtilityService } from 'src/app/services/utility/utility.service';
import { DataService } from './data-service.interface';
import { DATA_SERVICE } from './data-service.token';

interface Dettaglio {
  compiled: number;
  total: number;
  notCompiled?: number;
}

interface Notifiche {
  notified: number;
  sent: number;
}

interface Totali {
  sent: number;
  manual: number;
}

interface Mail {
  compiled: number;
  sent: number;
  notSent: number;
}

interface ValidationIro {
  validated: number;
  notValidated: number;
  validating: number;
}

export interface Elemento {
  azioni: Dettaglio;
  rispoteTesto: Dettaglio;
  kpi: Dettaglio;
  impatti: Dettaglio;
  impegni: Dettaglio;
  certificazioni: Dettaglio;
  tabelle: Dettaglio;
  manuali: Dettaglio;
  totali: Totali;
  mail: Mail;
  impattiIro: ValidationIro;
  rischi: ValidationIro;
  opportunita: ValidationIro;
  notifiche: Notifiche;
}

interface TotalePerElemento {
  color: string;
  valore: number;
  label: string;
}

interface CruscottoItem {
  key: string;
  titolo: string;
  nascondi: boolean;
  totaliPerElemento: TotalePerElemento[];
}

type TipoElemento =
  | 'KPI'
  | 'RISPOSTETESTO'
  | 'IMPATTI'
  | 'IMPEGNI'
  | 'AZIONI'
  | 'CERTIFICAZIONI'
  | 'TABELLE'
  | 'NOTIFICHE';

type TipoModulo =
  | 'MATERIALITA'
  | 'SINTESI'
  | 'BILANCIO'
  | 'POLICY'
  | 'PIANO'
  | 'DOPPIA_MATERIALITA'
  | 'REPORT_VSME';

type TipizzazioneElemento = 'dettaglio' | 'totali' | 'mail' | 'notifiche' | undefined;

@Component({
  selector: 'app-totali-cruscotto',
  standalone: false,

  templateUrl: './totali-cruscotto.component.html',
  styleUrl: './totali-cruscotto.component.scss',
})
export class TotaliCruscottoComponent {
  public elementiCruscotto?: Elemento;
  public totaliCruscotto: CruscottoItem[] = [];
  public isLoading = false;

  private _idModulo: string | undefined;
  @Input()
  set idModulo(idModulo: string | undefined) {
    this._idModulo = idModulo;
  }
  get idModulo() {
    return this._idModulo;
  }
  private _isIro: boolean = false;
  @Input()
  set isIro(isIro: boolean) {
    this._isIro = isIro;
  }
  get isIro() {
    return this._isIro;
  }

  private _tipoModulo?: TipoModulo;
  @Input()
  set tipoModulo(tipo: TipoModulo | undefined) {
    this._tipoModulo = tipo;
  }
  get tipoModulo() {
    return this._tipoModulo;
  }

  /**
   * Configurazione dei campi da mostrare nel cruscotto
   */
  public readonly CRUSCOTTO_CONFIG = {
    impatti: this._createCampiConfig('IMPATTI'),
    impegni: this._createCampiConfig('IMPEGNI'),
    azioni: this._createCampiConfig('AZIONI E BUONE PRATICHE', false),
    kpi: this._createCampiConfig('KPI'),
    certificazioni: this._createCampiConfig('CERTIFICAZIONI', false),
    risposteTesto: this._createCampiConfig('RISPOSTE TESTO', false),
    tabelle: this._createCampiConfig('TABELLE', false),
    manuali: this._createCampiConfig('MANUALI', true, '#c7a045'),
    mail: {
      titolo: 'MAIL',
      campi: [
        { key: 'compiled', label: 'compilate', color: '#c7a045' },
        { key: 'sent', label: 'inviate', color: '#729373' },
        { key: 'notSent', label: 'da inviare', color: '#cf5b61' },
      ],
    },
    totali: {
      titolo: 'TOTALI',
      campi: [
        { key: 'manual', label: 'manuali', color: 'var(--mat-color-accent)' },
        { key: 'sent', label: 'mail', color: 'var(--mat-color-accent)' },
      ],
    },
    impattiIro: this._createCampiConfigIRO('IMPATTI'),
    rischi: this._createCampiConfigIRO('RISCHI'),
    opportunita: this._createCampiConfigIRO('OPPORTUNITÀ'),
    notifiche: {
      titolo: 'NOTIFICHE',
      campi: [
        { key: 'notified', label: 'notificate', color: 'var(--mat-color-accent)' },
        { key: 'sent', label: 'inviate', color: '#729373' },
      ],
    },
  };

  constructor(
    @Inject(DATA_SERVICE) private readonly service: DataService,
    private readonly utilityService: UtilityService,
  ) {}

  /**
   * Crea la configurazione dei campi per una sezione del cruscotto per "CRUSCOTTO_CONFIG"
   * @param titolo Titolo della sezione
   * @param colorCompiled Colore per il campo "compilati"
   * @param colorNotCompiled Colore per il campo "non compilati"
   * @returns Oggetto di configurazione per la sezione
   */
  private _createCampiConfig(
    titolo: string,
    maschile: boolean = true,
    colorCompiled = '#729373',
    colorNotCompiled = '#cf5b61',
  ) {
    return {
      titolo,
      campi: [
        { key: 'compiled', label: maschile ? 'compilati' : 'compilate', color: colorCompiled },
        {
          key: 'notCompiled',
          label: maschile ? ' non compilati' : 'non compilate',
          color: colorNotCompiled,
        },
        { key: 'total', label: 'totali', color: 'var(--mat-color-accent)' },
      ],
    };
  }

  /**
   * Crea la configurazione dei campi per una sezione del cruscotto per "CRUSCOTTO_CONFIG"
   * @param titolo Titolo della sezione
   * @param colorCompiled Colore per il campo "compilati"
   * @param colorNotCompiled Colore per il campo "non compilati"
   * @returns Oggetto di configurazione per la sezione
   */
  private _createCampiConfigIRO(titolo: string) {
    return {
      titolo,
      campi: [
        { key: 'validated', label: 'validati', color: '#729373' },
        { key: 'validating', label: 'in validazione', color: '#c7a045' },
        {
          key: 'notValidated',
          label: 'non validati',
          color: '#cf5b61',
        },
      ],
    };
  }

  /**
   * Metodo generico per caricare i totali del cruscotto
   * @returns True se il caricamento è avvenuto con successo, false in caso di errore
   */
  public async getTotaliCruscotto(): Promise<boolean> {
    if (!this._idModulo) {
      this.utilityService.openSnackBar('Seleziona almeno una risposta', '', {
        duration: 2000,
        panelClass: ['red-snackbar'],
      });
      return Promise.reject(new Error('Id modulo non trovato'));
    }

    if (!this.service.getTotaliCruscottoDS) return false;

    try {
      this.isLoading = true;
      const totali: Elemento = await firstValueFrom(
        this.service.getTotaliCruscottoDS(this._idModulo, this.isIro),
      );

      this.elementiCruscotto = totali;

      this._filterTotali(this.elementiCruscotto);

      this.totaliCruscotto = this._mappaTotaliPerElementi(this.elementiCruscotto);

      return true;
    } catch (err) {
      console.error('Errore nel recupero dei totali cruscotto:', err);
      this.utilityService.openSnackBar('Errore totali cruscotto', '', {
        duration: 2000,
        panelClass: ['red-snackbar'],
      });
      return false;
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Filtra i valori di un oggetto `Elemento` e setta a `null`
   * le proprietà di `this.elementiCruscotto` che non rispettano le condizioni.
   * Questo metodo lo uso per non mostrare totali vuoti
   * @param {Elemento} totali - Oggetto contenente i dati (azioni, kpi, totali, mail, ecc.).
   */
  private _filterTotali(totali: Elemento) {
    Object.entries(totali).forEach(([chiave, configurazione]) => {
      if (!configurazione) {
        return;
      }

      switch (this.getTipo(configurazione)) {
        case 'dettaglio':
          if (!configurazione.compiled && !configurazione.total) {
            (this.elementiCruscotto as any)[chiave] = null;
          }
          break;

        case 'totali':
          if (!configurazione.sent && !configurazione.manual) {
            (this.elementiCruscotto as any)[chiave] = null;
          }
          break;

        case 'mail':
          if (!configurazione.notSent && !configurazione.sent) {
            (this.elementiCruscotto as any)[chiave] = null;
          }
          break;
        case 'notifiche':
          if (!configurazione.notified && !configurazione.sent) {
            (this.elementiCruscotto as any)[chiave] = null;
          }
          break;
      }
    });
  }

  /**
   * Ritorna il tipo di configurazione dell'elemento.
   *
   * @param {any} obj - Oggetto da tipizzare.
   * @returns {'dettaglio' | 'totali' | 'mail' | 'notifiche' | undefined}
   *  - 'dettaglio' se ha proprietà `compiled` e `total`
   *  - 'totali' se ha proprietà `sent` e `manual`
   *  - 'mail' se ha proprietà `compiled`, `sent`, `notSent`
   *  - 'notifiche' se ha proprietà `notified`, `sent`
   *  -  undefiend in tutti gli altri casi
   */
  private getTipo(obj: any): TipizzazioneElemento {
    if (obj && 'compiled' in obj && 'total' in obj) return 'dettaglio';
    if (obj && 'sent' in obj && 'manual' in obj) return 'totali';
    if (obj && 'compiled' in obj && 'sent' in obj && 'notSent' in obj) return 'mail';
    if (obj && 'notified' in obj && 'sent' in obj) return 'notifiche';
    return undefined;
  }

  /**
   * Mappa un oggetto Elemento nei totali per ogni sezione del cruscotto
   * flatMap = > chiave (kpi) configurazione ( color: string;  valore: number;  label: string;)
   * @param elementi Oggetto Elemento da mappare
   * @returns Array di CruscottoItem con i totali per ogni sezione
   */
  private _mappaTotaliPerElementi(elementi: Elemento): CruscottoItem[] {
    if (this._tipoModulo === 'PIANO') {
      this.CRUSCOTTO_CONFIG.azioni.titolo = 'T. QUALITATIVI';
      this.CRUSCOTTO_CONFIG.kpi.titolo = 'T. QUANTITATIVI';
    }

    return Object.entries(this.CRUSCOTTO_CONFIG).flatMap(([chiave, configurazione]) => {
      const elemento = (elementi as any)[chiave];

      if (!elemento) {
        return [];
      }

      const totaliPerElemento: TotalePerElemento[] = configurazione.campi
        // filtro fuori i campi con key === "total" perchè non va mostrato
        .filter((campo) => campo.key !== 'total')
        .map((campo) => {
          let valore: any;
          if (campo.key === 'notCompiled' && elemento.compiled !== undefined) {
            // Calcolo il valore degli elementi per i non compilati
            valore = this.calcolaNonCompilati(elemento.total, elemento.compiled);
          } else {
            //asengo il valore esistentete
            valore = elemento[campo.key] ?? 0;
          }

          return { color: campo.color, valore, label: campo.label };
        });

      return [
        {
          key: chiave.toUpperCase(),
          titolo: configurazione.titolo,
          nascondi: this._nascondiElemento(chiave.toUpperCase() as TipoElemento),
          totaliPerElemento,
        },
      ];
    });
  }

  private _nascondiElemento(chiaveElemento: TipoElemento): boolean {
    const nascondiImpatto: boolean =
      chiaveElemento === 'IMPATTI' && this._tipoModulo === 'BILANCIO';

    const nascondiNotificheIRO: boolean =
      chiaveElemento === 'NOTIFICHE' && this._tipoModulo === 'DOPPIA_MATERIALITA';

    if (nascondiImpatto || nascondiNotificheIRO) {
      return true;
    }

    return false;
  }

  /**
   * Calcola il numero di elementi "non compilati"
   * @param totale Numero totale di elementi
   * @param compilati Numero di elementi compilati
   * @returns Numero di elementi non compilati
   */
  calcolaNonCompilati(totale: number | null, compilati: number | null): number {
    return (totale ?? 0) - (compilati ?? 0);
  }

  /**
   * Controlla se ci sono elementi non compilati tra quelli indicati
   * @param keys Array di chiavi dei tipi di elemento da controllare
   * @returns True se almeno un elemento non è compilato, false altrimenti
   */
  public isElementiNonCompilati(keys: TipoElemento[]): boolean {
    if (!this.elementiCruscotto) return false;

    return keys.some((key) => {
      const obj = (this.elementiCruscotto as any)[key.toLowerCase()];
      if (obj?.total !== undefined && obj?.compiled !== undefined) {
        return this.calcolaNonCompilati(obj.total, obj.compiled) > 0;
      }
      return false;
    });
  }
}
